import React, { useState, useRef } from "react";
import ReactDOM from "react-dom";
import { api, authFetch } from "../utils/api";
import { NavigateFunction } from "react-router-dom";
import { t } from "../utils/i18n";
import { formatScore, relativeDayLabel } from "../utils/format";
import VerifiedBadge from "./VerifiedBadge";
import ReactorsListModal from "./ReactorsListModal";
import SponsorModal from "./SponsorModal";
import { ACCENT, TRANSITION_FAST } from "../styles/tokens";

export const COVER_COLORS: Record<string, string> = {
  passion: "linear-gradient(135deg, #0a0a0a 0%, #333333 100%)",
  power: "linear-gradient(135deg, #333333 0%, #1a1a1a 100%)",
  ritual: "linear-gradient(135deg, #555555 0%, #1a1a1a 100%)",
  community: "linear-gradient(135deg, #333333 0%, #1a1a1a 100%)",
};
export const TYPE_COLORS: Record<string, string> = {
  passion: "#0a0a0a", power: "#333333", ritual: "#555555", community: "#333333", feedback: "#0a0a0a",
};

// Le libellé du bouton d'action s'adapte à l'activité elle-même plutôt que d'être
// un "Participer" générique partout : un covoiturage ne se "rejoint" pas comme une sortie.
const CATEGORY_ACTION_KEYS: Record<string, string> = {
  "Covoiturage": "move_request_seat",
  "Transport": "move_request_seat",
  "Baby-sitting": "book",
  "Garde d'animaux": "book",
  "Ménage": "book",
  "Coaching sportif": "book",
};
const actionLabel = (post: any, lang: string) => t(lang, CATEGORY_ACTION_KEYS[post.category] || (post.type === "power" ? "book" : "pc_participate"));
const LOCATION_TYPE_KEYS: Record<string, string> = { home: "cr_at_client", remote: "cr_at_remote" };
const isSponsored = (post: any) => !!post.is_sponsored && !!post.boosted_until && new Date(post.boosted_until) > new Date();
const isPastEvent = (post: any) => !!post.event_date && new Date(post.event_date) < new Date();
// Style "19h" / "15h30" plutôt que "19:00" — l'heure est toujours présente dans event_date
// (le sélecteur de création n'a pas d'option "sans heure"), donc pas besoin de la masquer.
const formatEventTime = (dateStr: string) => {
  const d = new Date(dateStr);
  const h = d.getHours();
  const m = d.getMinutes();
  return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, "0")}`;
};
// Seules les passions/powers ont une fiche détail dédiée (agenda, itinéraire, participants) —
// les rituels/communautés ont chacun leur propre page, pas de route /publication pour eux.
const isBookable = (post: any) => post.type === "passion" || post.type === "power";

// Initiales à partir d'un nom (sécurisé si nom manquant)
const initials = (name?: string) =>
  (name || "?").trim().split(" ").map(n => n[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";

// Temps relatif ("À l'instant", "3h", "2j"...) à partir de la date réelle de publication
const timeAgo = (dateStr: string | undefined, lang: string) => {
  if (!dateStr) return "";
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return t(lang, "now");
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}j`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo} mois`;
  return `${Math.floor(mo / 12)} an${Math.floor(mo / 12) > 1 ? "s" : ""}`;
};

// Icônes SVG épurées (traits fins, monochrome) — cohérentes avec le reste d'OLTODO.
const ShareIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
    <line x1="8.6" y1="13.5" x2="15.4" y2="17.5"/><line x1="15.4" y1="6.5" x2="8.6" y2="10.5"/>
  </svg>
);
const CommentIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 11.5a8.4 8.4 0 0 1-8.4 8.4 8.3 8.3 0 0 1-3.8-.9L3 21l1.9-5.8a8.3 8.3 0 0 1-.9-3.8A8.4 8.4 0 0 1 12.5 3a8.4 8.4 0 0 1 8.4 8.4z"/>
  </svg>
);
// Réaction "Envieux" sur un feedback — une flamme plutôt qu'un emoji, pour rester dans le
// même langage graphique épuré que le reste de l'appli.
const FlameIcon = ({ filled = false }: { filled?: boolean }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/>
  </svg>
);
const MicIcon = ({ color = "white" }: { color?: string }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="2" width="6" height="12" rx="3"/><path d="M5 10v1a7 7 0 0 0 14 0v-1"/><line x1="12" y1="19" x2="12" y2="22"/>
  </svg>
);
const SendIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
  </svg>
);
const KebabIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="12" cy="5" r="1.8"/><circle cx="12" cy="12" r="1.8"/><circle cx="12" cy="19" r="1.8"/>
  </svg>
);
const InviteIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/>
    <line x1="19" y1="8" x2="19" y2="14"/><line x1="16" y1="11" x2="22" y2="11"/>
  </svg>
);
const PlayIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="white"><polygon points="6 3 21 12 6 21 6 3"/></svg>
);
const PauseIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="white"><rect x="5" y="3" width="5" height="18" rx="1"/><rect x="14" y="3" width="5" height="18" rx="1"/></svg>
);
const PencilIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>
  </svg>
);
const TrashIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
  </svg>
);

const VOICE_COMMENT_MAX_MS = 10000;
const formatAudioTime = (s: number) => `0:${Math.max(0, Math.round(s || 0)).toString().padStart(2, "0")}`;

// Lecteur audio épuré pour les commentaires vocaux — remplace le <audio controls> natif du
// navigateur (moche, incohérent entre navigateurs) par un bouton play/pause + barre + durée.
function CommentAudioPlayer({ src }: { src: string }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) a.pause(); else a.play().catch(() => {});
  };

  const progress = duration > 0 ? current / duration : 0;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 9, backgroundColor: "#F5F5F5", borderRadius: 20, padding: "6px 12px 6px 6px", marginTop: 4, maxWidth: 200 }}>
      <audio
        ref={audioRef} src={src} preload="metadata" style={{ display: "none" }}
        onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)}
        onLoadedMetadata={e => setDuration(e.currentTarget.duration || 0)}
        onTimeUpdate={e => setCurrent(e.currentTarget.currentTime)}
      />
      <button onClick={toggle} style={{ width: 26, height: 26, borderRadius: "50%", border: "none", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
        {playing ? <PauseIcon /> : <PlayIcon />}
      </button>
      <div style={{ flex: 1, height: 3, borderRadius: 2, backgroundColor: "#E0E0E0", overflow: "hidden", minWidth: 50 }}>
        <div style={{ height: "100%", width: `${Math.min(100, progress * 100)}%`, backgroundColor: "#0a0a0a" }} />
      </div>
      <span style={{ fontSize: 10.5, color: "#999", fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
        {formatAudioTime(playing || current > 0 ? current : duration)}
      </span>
    </div>
  );
}

// Inviter des amis à une publication déjà en ligne (passion/power) — un choix rapide d'avatars,
// même logique que le sélecteur d'amis à la création, réutilisé ici pour un partage ciblé.
function InvitePostModal({ postId, onClose, lang }: { postId: string; onClose: () => void; lang: string }) {
  const [friends, setFriends] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  React.useEffect(() => {
    api.get(`/publications/${postId}/invite-candidates`).then(data => setFriends(data?.friends || [])).catch(() => {}).finally(() => setLoading(false));
  }, [postId]);

  const toggle = (id: string) => setSelected(prev => prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]);

  const submit = () => {
    if (selected.length === 0 || sending) return;
    setSending(true);
    api.post(`/publications/${postId}/invite`, { invited_user_ids: selected }).then(() => {
      setSent(true);
      setTimeout(onClose, 1200);
    }).catch(() => {}).finally(() => setSending(false));
  };

  // Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un position:fixed
  // descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:"touch") se positionne
  // sur Safari iOS par rapport au contenu défilant plutôt qu'au vrai viewport.
  return ReactDOM.createPortal(
    <div onClick={onClose} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.6)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: 20, padding: 22, width: "100%", maxWidth: 380, maxHeight: "80vh", overflowY: "auto", boxSizing: "border-box" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <span style={{ fontSize: 17, fontWeight: 700 }}>{t(lang, "bday_invite_friends_label")}</span>
          <button onClick={onClose} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#999" }}>✕</button>
        </div>
        {sent ? (
          <div style={{ fontSize: 14, color: "#333", padding: "20px 0", textAlign: "center" }}>{t(lang, "pc_invitation_sent")}</div>
        ) : loading ? (
          <div style={{ fontSize: 13, color: "#999", padding: "8px 0" }}>{t(lang, "cr_loading_friends")}</div>
        ) : friends.length === 0 ? (
          <div style={{ fontSize: 13, color: "#999", padding: "8px 0" }}>{t(lang, "cr_no_friends_invite")}</div>
        ) : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(60px, 1fr))", gap: 14, marginBottom: 18 }}>
              {friends.map(friend => (
                <div key={friend.id} onClick={() => toggle(friend.id)} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, cursor: "pointer" }}>
                  <div style={{ width: 48, height: 48, borderRadius: "50%", position: "relative", border: selected.includes(friend.id) ? "3px solid #0a0a0a" : "3px solid transparent" }}>
                    <div style={{ width: "100%", height: "100%", borderRadius: "50%", overflow: "hidden", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 13 }}>
                      {friend.avatar_url ? <img src={friend.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : (friend.name || "?").split(" ").map((n: string) => n[0]).join("").slice(0, 2).toUpperCase()}
                    </div>
                    {selected.includes(friend.id) && <div style={{ position: "absolute", bottom: -2, right: -2, width: 16, height: 16, borderRadius: "50%", backgroundColor: "#0a0a0a", border: "2px solid white", display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ color: "white", fontSize: 9 }}>✓</span></div>}
                  </div>
                  <span style={{ fontSize: 10, color: selected.includes(friend.id) ? "#0a0a0a" : "#999", fontWeight: selected.includes(friend.id) ? 600 : 400, textAlign: "center" }}>{(friend.name || "?").split(" ")[0]}</span>
                </div>
              ))}
            </div>
            <button onClick={submit} disabled={selected.length === 0 || sending} style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 14, fontWeight: 600, cursor: "pointer", opacity: (selected.length === 0 || sending) ? 0.5 : 1 }}>
              {sending ? t(lang, "msg_sending_short") : `${t(lang, "date_prompt_cta")}${selected.length > 0 ? ` (${selected.length})` : ""}`}
            </button>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

// Bouton de la rangée d'actions — largeur égale entre tous les boutons présents (flex:1) et
// hauteur fixe, pour qu'un changement d'état (compteur qui apparaît, libellé qui change) ne
// fasse jamais bouger ou redimensionner la rangée : seul le contenu interne se tronque.
// Bouton "arrêter l'enregistrement" avec anneau de progression (jusqu'à VOICE_COMMENT_MAX_MS) —
// remplace l'ancien simple bouton micro rouge, pour qu'on voie concrètement combien de temps
// il reste avant l'arrêt automatique, et qu'on comprenne que taper dessus arrête (puis prévisualise).
const RecordRingButton = ({ progress, onClick, lang }: { progress: number; onClick: () => void; lang: string }) => {
  const size = 34, stroke = 3, r = (size - stroke) / 2, c = 2 * Math.PI * r;
  return (
    <button onClick={onClick} title={t(lang, "pc_stop_recording")} style={{ position: "relative", width: size, height: size, borderRadius: "50%", border: "none", background: "none", cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
      <svg width={size} height={size} style={{ position: "absolute", top: 0, left: 0, transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e6b9b9" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#c0392b" strokeWidth={stroke} strokeDasharray={c} strokeDashoffset={c * (1 - Math.min(1, progress))} strokeLinecap="round" />
      </svg>
      <div style={{ width: 11, height: 11, borderRadius: 3, backgroundColor: "#c0392b" }} />
    </button>
  );
};

const ActionButton = ({ active, primary, disabled, onClick, icon, children }: {
  active?: boolean; primary?: boolean; disabled?: boolean; onClick?: () => void; icon?: React.ReactNode; children: React.ReactNode;
}) => {
  const [hovered, setHovered] = useState(false);
  // Le survol bleu ne s'applique qu'à l'état "contour" (ni rempli/actif, ni désactivé) — une
  // fois cliqué, le bouton reste noir (voir Layout.tsx : même principe, le bleu ne remplace
  // jamais l'état sélectionné/actif, juste "en train de pointer").
  const filled = primary || active;
  const showHover = hovered && !disabled && !filled;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        flex: 1, minWidth: 0, height: 40,
        display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
        padding: "0 10px", borderRadius: 20,
        border: disabled ? "1.5px solid #EDEDED" : (filled ? "none" : `1.5px solid ${showHover ? ACCENT : "#E2E2E2"}`),
        backgroundColor: disabled ? "#F5F5F5" : (filled ? "#0a0a0a" : "white"),
        color: disabled ? "#B5B5B5" : (filled ? "white" : showHover ? ACCENT : "#555"),
        fontSize: 13, fontWeight: 600,
        cursor: disabled ? "default" : "pointer",
        transition: TRANSITION_FAST,
      }}
    >
      {icon}
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, fontVariantNumeric: "tabular-nums" }}>{children}</span>
    </button>
  );
};

// Carte de publication unique, utilisée partout (fil d'accueil, mon profil, profil visité)
// pour un rendu et des actions strictement identiques. Modifier/Supprimer n'apparaissent que
// si onEdit/onDelete sont fournis ET que l'utilisateur courant est bien l'auteur.
const REPORT_REASON_KEYS = ["pc_reason_inappropriate", "pc_reason_scam", "pc_reason_fake", "date_activity_other"];

export default function PostCard({ post, lang, user, navigate, toggleReaction, participate, onEdit, onDelete, onHideAuthor, extraFooter }: {
  post: any; lang: string; user: any; navigate: NavigateFunction;
  toggleReaction: (post: any, type: "interested" | "top" | "envieux") => void;
  participate?: (post: any) => void;
  onEdit?: (post: any) => void;
  onDelete?: (post: any) => void;
  // Appelé une fois "Masquer les publications de X" confirmé — permet à l'écran parent (fil,
  // profil...) de retirer immédiatement toutes les cartes de cet auteur de la liste déjà
  // affichée (le blocage lui-même exclut déjà cet auteur des PROCHAINS chargements de fil).
  onHideAuthor?: (userId: string) => void;
  extraFooter?: React.ReactNode;
}) {
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState<any[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [replyingTo, setReplyingTo] = useState<{ id: string; user_name: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordMs, setRecordMs] = useState(0);
  const [voicePreview, setVoicePreview] = useState<{ url: string; blob: Blob } | null>(null);
  const [sendingVoice, setSendingVoice] = useState(false);
  const [shareConfirm, setShareConfirm] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showSponsorModal, setShowSponsorModal] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [reactorsType, setReactorsType] = useState<"top" | "envieux" | "interested" | null>(null);
  const [openCommentMenu, setOpenCommentMenu] = useState<string | null>(null);
  const [showViewerMenu, setShowViewerMenu] = useState(false);
  const [showReportPicker, setShowReportPicker] = useState(false);
  const [reportSent, setReportSent] = useState(false);
  const [hidingAuthor, setHidingAuthor] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editCommentText, setEditCommentText] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const isGroupCard = post.type === "community" || post.type === "ritual";
  const isWelcomeCard = post.type === "welcome";
  const groupInfo: any = post.type === "community" ? post.community_info : post.type === "ritual" ? post.ritual_info : null;
  // Privé/sur invitation : 'adhesion' pour une communauté (binaire public/invitation), 'audience'
  // pour un rituel (public/amis/prive) — voir POST /communities|rituals/:id/join côté serveur,
  // qui bascule sur une demande à valider plutôt qu'un rejoint immédiat dans ce cas.
  const isPrivateGroup = post.type === "community" ? !!groupInfo?.adhesion && groupInfo.adhesion !== "public" : !!groupInfo?.audience && groupInfo.audience !== "public";
  const [groupStatus, setGroupStatus] = useState<"none" | "pending" | "joined">(
    groupInfo?.my_status === "active" || (!groupInfo?.my_status && groupInfo?.my_role) ? "joined" : groupInfo?.my_status === "pending" ? "pending" : "none"
  );
  const [groupJoining, setGroupJoining] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cancelledRecordingRef = useRef(false);
  const commentsCount = post.comments_count || comments.length;

  React.useEffect(() => () => { if (voicePreview) URL.revokeObjectURL(voicePreview.url); }, [voicePreview]);

  const toggleComments = () => {
    setShowComments(v => !v);
    if (!showComments && comments.length === 0) {
      setLoadingComments(true);
      api.get(`/publications/${post.id}/comments`).then(data => setComments(data?.comments || [])).catch(() => {}).finally(() => setLoadingComments(false));
    }
  };

  const submitTextComment = () => {
    if (!commentText.trim() || submitting) return;
    setSubmitting(true);
    api.post(`/publications/${post.id}/comments`, { content: commentText.trim(), type: "text", parent_id: replyingTo?.id }).then(data => {
      if (data?.comment) { setComments(prev => [...prev, data.comment]); setCommentText(""); setReplyingTo(null); }
      else if (data?.error) alert(data.error);
    }).catch(() => {}).finally(() => setSubmitting(false));
  };

  const startEditComment = (c: any) => {
    setEditingCommentId(c.id);
    setEditCommentText(c.content);
    setOpenCommentMenu(null);
  };
  const cancelEditComment = () => { setEditingCommentId(null); setEditCommentText(""); };
  const saveEditComment = async (c: any) => {
    if (!editCommentText.trim() || savingEdit) return;
    setSavingEdit(true);
    try {
      const data = await api.patch(`/publications/${post.id}/comments/${c.id}`, { content: editCommentText.trim() });
      // Le PATCH ne renvoie que les colonnes de la table comments (pas de jointure user) —
      // on fusionne pour garder le nom/avatar déjà en mémoire plutôt que les perdre.
      if (data?.comment) { setComments(prev => prev.map(x => (x.id === c.id ? { ...x, ...data.comment } : x))); cancelEditComment(); }
      else if (data?.error) alert(data.error);
    } catch { alert(t(lang, "err_connection_short")); }
    finally { setSavingEdit(false); }
  };
  const deleteComment = async (c: any) => {
    setOpenCommentMenu(null);
    if (!window.confirm("Supprimer ce commentaire ?")) return;
    try {
      const data = await api.delete(`/publications/${post.id}/comments/${c.id}`);
      if (data?.message) setComments(prev => prev.filter(x => x.id !== c.id && x.parent_id !== c.id));
      else if (data?.error) alert(data.error);
    } catch { alert(t(lang, "err_connection_short")); }
  };

  const stopRecording = () => {
    if (recordTimerRef.current) { clearInterval(recordTimerRef.current); recordTimerRef.current = null; }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") mediaRecorderRef.current.stop();
    setRecording(false);
  };

  // Contrairement à stopRecording (qui mène à l'écoute avant envoi), annuler jette
  // directement l'enregistrement en cours — aucune prévisualisation à nettoyer ensuite.
  const cancelRecording = () => {
    cancelledRecordingRef.current = true;
    stopRecording();
    setRecordMs(0);
  };

  const startRecording = async () => {
    if (recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      audioChunksRef.current = [];
      recorder.ondataavailable = e => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = () => {
        stream.getTracks().forEach(tr => tr.stop());
        if (cancelledRecordingRef.current) { cancelledRecordingRef.current = false; return; }
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        // Jamais d'envoi automatique à l'arrêt — l'utilisateur doit pouvoir réécouter et
        // confirmer explicitement (ou recommencer) avant que le vocal parte vraiment.
        if (blob.size >= 500) setVoicePreview({ url: URL.createObjectURL(blob), blob });
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setRecording(true);
      setRecordMs(0);
      recordTimerRef.current = setInterval(() => {
        setRecordMs(ms => {
          const next = ms + 200;
          if (next >= VOICE_COMMENT_MAX_MS) { stopRecording(); return VOICE_COMMENT_MAX_MS; }
          return next;
        });
      }, 200);
    } catch {
      alert(t(lang, "msg_error_mic"));
    }
  };

  const discardVoicePreview = () => {
    if (voicePreview) URL.revokeObjectURL(voicePreview.url);
    setVoicePreview(null);
    setRecordMs(0);
  };

  const sendVoicePreview = async () => {
    if (!voicePreview || sendingVoice) return;
    setSendingVoice(true);
    try {
      const fd = new FormData();
      fd.append("file", new File([voicePreview.blob], "comment.webm", { type: "audio/webm" }));
      const res = await authFetch("/uploads/comment", { method: "POST", body: fd });
      const data = await res.json();
      if (data?.url) {
        const c = await api.post(`/publications/${post.id}/comments`, { type: "voice", media_url: data.url, parent_id: replyingTo?.id });
        if (c?.comment) {
          setComments(prev => [...prev, c.comment]);
          setReplyingTo(null);
          discardVoicePreview();
        } else if (c?.error) {
          alert(c.error);
        }
      }
    } catch {
      alert(t(lang, "msg_error_upload_voice"));
    } finally {
      setSendingVoice(false);
    }
  };

  const share = async () => {
    const shareData = { title: post.title, text: post.description ? `${post.title}, ${post.description}` : post.title, url: `${window.location.origin}/publication/${post.id}` };
    if ((navigator as any).share) {
      try { await (navigator as any).share(shareData); return; } catch { return; /* annulé par l'utilisateur */ }
    }
    const fullText = `${shareData.text} (via OLTODO, ${shareData.url})`;
    try {
      await navigator.clipboard.writeText(fullText);
      setShareConfirm(true);
      setTimeout(() => setShareConfirm(false), 2000);
    } catch {
      // Permission presse-papier indisponible (ancien navigateur, contexte non sécurisé...) :
      // on retombe sur une méthode de copie compatible partout plutôt que d'échouer en silence.
      const textarea = document.createElement("textarea");
      textarea.value = fullText;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      try { document.execCommand("copy"); setShareConfirm(true); setTimeout(() => setShareConfirm(false), 2000); }
      catch { window.prompt("Copie le lien pour partager :", fullText); }
      document.body.removeChild(textarea);
    }
  };

  const joinGroup = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (groupJoining || groupStatus !== "none" || !groupInfo) return;
    setGroupJoining(true);
    const path = post.type === "community" ? `/communities/${groupInfo.id}/join` : `/rituals/${groupInfo.id}/join`;
    api.post(path, {}).then((data: any) => setGroupStatus(data?.pending ? "pending" : "joined")).catch(() => {}).finally(() => setGroupJoining(false));
  };

  // Signaler une publication (menu visible à tout viewer, en haut à gauche de la carte) — pas
  // de modération automatique, juste une trace exploitable par l'équipe OLTODO. "Masquer"
  // réutilise le blocage déjà existant (POST /users/:id/block) : le blocage étant mutuel, il
  // exclut aussi cet auteur des prochains chargements du fil (voir algorithm.ts) — onHideAuthor
  // ne sert qu'à retirer tout de suite ses cartes déjà affichées, sans attendre un rechargement.
  const submitReport = (reason: string) => {
    api.post(`/publications/${post.id}/report`, { reason }).then(() => setReportSent(true)).catch(() => {});
  };
  const hideAuthor = () => {
    if (hidingAuthor) return;
    setHidingAuthor(true);
    api.post(`/users/${post.user_id}/block`, {}).then(() => {
      setShowViewerMenu(false);
      onHideAuthor?.(post.user_id);
    }).catch(() => {}).finally(() => setHidingAuthor(false));
  };

  const isFeedback = post.type === "feedback";
  // L'édition/suppression reste un droit de l'auteur réel (user_id), jamais de l'identité
  // affichée (display_id) — sinon un admin perdrait ses droits sur sa propre publication
  // publiée au nom d'un lieu/communauté/rituel.
  const isOwner = !!user?.id && post.user_id === user.id;
  const goToIdentity = () => {
    const id = post.display_id || post.user_id;
    if (post.display_type === "venue") navigate("/venue/" + id);
    else if (post.display_type === "community") navigate("/community/" + id);
    else if (post.display_type === "ritual") navigate("/ritual/" + id);
    else navigate("/profile/" + id);
  };
  // Fallback pour les feedbacks créés avant la galerie multi-photos : une seule cover_url.
  const feedbackMedia: { url: string; media_type: string }[] =
    post.photos && post.photos.length > 0 ? post.photos : (post.cover_url ? [{ url: post.cover_url, media_type: "image" }] : []);

  // Un commentaire (ou une réponse, en plus petit/indenté) — factorisé pour éviter la duplication
  // entre le commentaire parent et ses réponses. Modifier/Supprimer n'apparaissent que pour
  // l'auteur du commentaire ; Modifier est réservé au texte (un vocal ne se réécrit pas).
  const renderComment = (c: any, small?: boolean) => {
    const isCommentOwner = !!user?.id && c.user_id === user.id;
    const isEditing = editingCommentId === c.id;
    const edited = !!c.updated_at && !!c.created_at && new Date(c.updated_at).getTime() > new Date(c.created_at).getTime();
    return (
      <div key={c.id} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: small ? "8px 0 8px 40px" : "8px 0" }}>
        <div style={{ width: small ? 24 : 30, height: small ? 24 : 30, borderRadius: "50%", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: small ? 9.5 : 11, overflow: "hidden", flexShrink: 0 }}>
          {c.user_avatar ? <img src={c.user_avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(c.user_name)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <div style={{ fontSize: small ? 11.5 : 12, fontWeight: 700 }}>{c.user_name}</div>
            {isCommentOwner && !isEditing && (
              <div style={{ position: "relative", flexShrink: 0 }}>
                <button onClick={() => setOpenCommentMenu(v => (v === c.id ? null : c.id))} style={{ border: "none", background: "none", cursor: "pointer", padding: 3, color: "#bbb", display: "flex" }}>
                  <KebabIcon />
                </button>
                {openCommentMenu === c.id && (
                  <>
                    <div onClick={() => setOpenCommentMenu(null)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
                    <div style={{ position: "absolute", top: "110%", right: 0, backgroundColor: "white", border: "1px solid #F0F0F0", borderRadius: 10, boxShadow: "0 4px 16px rgba(0,0,0,0.14)", overflow: "hidden", zIndex: 50, minWidth: 130 }}>
                      {c.type === "text" && (
                        <button onClick={() => startEditComment(c)} style={{ display: "flex", alignItems: "center", gap: 7, width: "100%", textAlign: "left", padding: "9px 13px", border: "none", background: "none", cursor: "pointer", fontSize: 12.5, fontWeight: 600, color: "#0a0a0a" }}>
                          <PencilIcon /> {t(lang, "edit")}
                        </button>
                      )}
                      <button onClick={() => deleteComment(c)} style={{ display: "flex", alignItems: "center", gap: 7, width: "100%", textAlign: "left", padding: "9px 13px", border: "none", background: "none", cursor: "pointer", fontSize: 12.5, fontWeight: 600, color: "#c0392b", borderTop: c.type === "text" ? "1px solid #F5F5F5" : "none" }}>
                        <TrashIcon /> {t(lang, "delete")}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {isEditing ? (
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
              <input value={editCommentText} onChange={e => setEditCommentText(e.target.value)} onKeyDown={e => e.key === "Enter" && saveEditComment(c)} autoFocus style={{ flex: 1, padding: "6px 10px", borderRadius: 14, border: "1px solid #E0E0E0", fontSize: small ? 12.5 : 13, outline: "none" }} />
              <button onClick={() => saveEditComment(c)} disabled={savingEdit} style={{ border: "none", background: "none", cursor: "pointer", color: "#0a0a0a", fontSize: 12, fontWeight: 700 }}>OK</button>
              <button onClick={cancelEditComment} style={{ border: "none", background: "none", cursor: "pointer", color: "#999", fontSize: 12 }}>{t(lang, "cancel")}</button>
            </div>
          ) : c.type === "voice" ? (
            <CommentAudioPlayer src={c.media_url} />
          ) : (
            <div style={{ fontSize: small ? 12.5 : 13, color: "#333", lineHeight: 1.5 }}>
              {c.content}{edited && <span style={{ fontSize: 10.5, color: "#bbb", marginLeft: 5 }}>{t(lang, "pc_edited_paren")}</span>}
            </div>
          )}

          {!isEditing && !small && (
            <button onClick={() => setReplyingTo({ id: c.id, user_name: c.user_name })} style={{ border: "none", background: "none", cursor: "pointer", padding: 0, marginTop: 3, fontSize: 11.5, fontWeight: 700, color: "#999" }}>
              {t(lang, "pc_reply")}
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ position: "relative", backgroundColor: "white", borderRadius: 20, marginBottom: 16, overflow: "hidden", border: "1px solid #F0F0F0", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", animation: "oltodo-card-in 250ms ease-out" }}>
      {/* Apparition en fondu au montage — chaque carte ne monte qu'une fois par item du fil,
          donc cette animation ne rejoue jamais après coup (pas de re-render bruyant). Même
          convention (keyframe scopé, déclaré au plus près de l'usage) que oltodo-pulse ci-dessous. */}
      <style>{`@keyframes oltodo-card-in { from { opacity: 0; transform: translateY(8px) } to { opacity: 1; transform: translateY(0) } }`}</style>

      {/* Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un position:fixed
          descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:"touch") se positionne
          sur Safari iOS par rapport au contenu défilant plutôt qu'au vrai viewport. */}
      {showReportPicker && ReactDOM.createPortal(
        <div onClick={() => { setShowReportPicker(false); setReportSent(false); }} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: 18, padding: 20, width: "100%", maxWidth: 340, boxSizing: "border-box" }}>
            {reportSent ? (
              <div style={{ textAlign: "center", padding: "10px 0" }}>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{t(lang, "pc_report_sent")}</div>
                <div style={{ fontSize: 12.5, color: "#999" }}>{t(lang, "pc_report_sent_sub")}</div>
              </div>
            ) : (
              <>
                <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>{t(lang, "pc_report_why")}</div>
                {REPORT_REASON_KEYS.map(rk => (
                  <button key={rk} onClick={() => submitReport(t(lang, rk))} style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", background: "white", marginBottom: 8, cursor: "pointer", fontSize: 13 }}>
                    {t(lang, rk)}
                  </button>
                ))}
              </>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Header auteur — toujours l'identité choisie à la publication (display_*), jamais
          author_* directement : retombe sur l'auteur réel quand publié en personnel. */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px 10px" }}>
        <div onClick={goToIdentity} style={{ width: 46, height: 46, borderRadius: "50%", backgroundColor: "#0a0a0a", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 15, overflow: "hidden", cursor: "pointer" }}>
          {post.display_avatar ? <img src={post.display_avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(post.display_name)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, rowGap: 0 }}>
            <span onClick={goToIdentity} style={{ fontWeight: 700, fontSize: 15, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}>{post.display_name || post.author_name || t(lang, "default_user_name")}</span>
            {post.display_verified && <VerifiedBadge size={14} />}
            <span style={{ color: "#999", fontSize: 13, whiteSpace: "nowrap", flexShrink: 0 }}>
              · {isFeedback ? t(lang, "pc_shared_feedback") : isGroupCard ? (post.type === "community" ? t(lang, "pc_created_community") : t(lang, "pc_created_ritual")) : isWelcomeCard ? t(lang, "onboarding_welcome_joined_suffix") : post.category}
            </span>
          </div>
          <div style={{ fontSize: 12, color: "#bbb", marginTop: 2 }}>
            {isGroupCard || isWelcomeCard ? timeAgo(post.created_at, lang) : `${post.type === "power" && ["home", "remote"].includes(post.location_type) ? t(lang, LOCATION_TYPE_KEYS[post.location_type]) : (post.location_name || "—")} · ${timeAgo(post.created_at, lang)}`}
          </div>
        </div>
        {isSponsored(post) && (
          <div style={{ backgroundColor: "#FFF7E0", border: "1px solid #F0D98C", borderRadius: 20, padding: "5px 10px", fontSize: 11, fontWeight: 700, color: "#8a6d1a" }}>
            {t(lang, "pc_sponsored")}
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
          {/* Menu viewer (Signaler / Masquer) — discret, juste au-dessus du score, distinct du
              menu Modifier/Booster/Supprimer de l'auteur, jamais affiché sur sa propre
              publication, jamais superposé à la photo de profil. */}
          {!isOwner && (
            <div style={{ position: "relative" }}>
              <button onClick={() => setShowViewerMenu(v => !v)} style={{ border: "none", background: "none", cursor: "pointer", padding: 2, display: "flex", color: "#bbb" }}>
                <KebabIcon />
              </button>
              {showViewerMenu && (
                <>
                  <div onClick={() => setShowViewerMenu(false)} style={{ position: "fixed", inset: 0, zIndex: 20 }} />
                  <div style={{ position: "absolute", top: "115%", right: 0, backgroundColor: "white", border: "1px solid #F0F0F0", borderRadius: 12, boxShadow: "0 4px 20px rgba(0,0,0,0.14)", overflow: "hidden", zIndex: 30, minWidth: 210 }}>
                    <button onClick={() => { setShowViewerMenu(false); setShowReportPicker(true); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "11px 16px", border: "none", background: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#0a0a0a" }}>
                      {t(lang, "pc_report")}
                    </button>
                    <button onClick={hideAuthor} disabled={hidingAuthor} style={{ display: "block", width: "100%", textAlign: "left", padding: "11px 16px", border: "none", background: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#c0392b", borderTop: "1px solid #F5F5F5" }}>
                      {t(lang, "pc_hide_posts_of").replace("{name}", (post.author_name || t(lang, "pc_this_person")).split(" ")[0])}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
          <div style={{ backgroundColor: "#F5F5F5", borderRadius: 20, padding: "5px 12px", fontSize: 12, fontWeight: 700, color: "#0a0a0a" }}>
            ★ {formatScore(post.display_rating ?? post.author_score)}
          </div>
        </div>
        {isOwner && !isGroupCard && !isWelcomeCard && (onEdit || onDelete) && (
          <div style={{ position: "relative", flexShrink: 0 }}>
            <button onClick={() => setShowMenu(v => !v)} style={{ width: 30, height: 30, borderRadius: "50%", border: "none", background: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#999" }}>
              <KebabIcon />
            </button>
            {showMenu && (
              <>
                <div onClick={() => setShowMenu(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
                <div style={{ position: "absolute", top: "110%", right: 0, backgroundColor: "white", border: "1px solid #F0F0F0", borderRadius: 12, boxShadow: "0 4px 20px rgba(0,0,0,0.14)", overflow: "hidden", zIndex: 50, minWidth: 150 }}>
                  {onEdit && (
                    <button onClick={() => { setShowMenu(false); onEdit(post); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "11px 16px", border: "none", background: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#0a0a0a" }}>
                      {t(lang, "edit")}
                    </button>
                  )}
                  <button onClick={() => { setShowMenu(false); setShowSponsorModal(true); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "11px 16px", border: "none", background: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#0a0a0a", borderTop: onEdit ? "1px solid #F5F5F5" : "none" }}>
                    {t(lang, "pc_boost")}
                  </button>
                  {onDelete && (
                    <button onClick={() => { setShowMenu(false); onDelete(post); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "11px 16px", border: "none", background: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#c0392b", borderTop: "1px solid #F5F5F5" }}>
                      {t(lang, "delete")}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Description — la carte de bienvenue affiche son propre bloc mis en page plus bas */}
      {post.description && !isWelcomeCard && (
        <div style={{ padding: "0 16px 12px", fontSize: 14, color: "#444", lineHeight: 1.7 }}>{post.description}</div>
      )}

      {/* Personnes mentionnées (feedback) — miniatures cliquables vers leur profil, même langage
          visuel que la pile de participants confirmés/membres plus bas dans ce fichier, plutôt
          que le simple texte "avec Prénom Nom" d'avant (ni cliquable, ni reconnaissable). */}
      {post.mentions && post.mentions.length > 0 && (
        <div style={{ padding: "0 16px 12px", display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13, color: "#666" }}>{t(lang, "pc_with")}</span>
          <div style={{ display: "flex" }}>
            {post.mentions.map((m: any, i: number) => (
              <div
                key={m.id}
                onClick={e => { e.stopPropagation(); navigate(`/profile/${m.id}`); }}
                title={m.name}
                style={{ width: 28, height: 28, borderRadius: "50%", border: "2px solid white", boxShadow: "0 0 0 1px #E5E5E5", marginLeft: i > 0 ? -10 : 0, backgroundColor: "#333", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 10, fontWeight: 700, cursor: "pointer" }}
              >
                {m.avatar_url ? <img src={m.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(m.name)}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Un feedback affiche un vrai carrousel de souvenirs (plusieurs photos/vidéos, façon
          Facebook/LinkedIn), pas la vignette "annonce d'activité" avec catégorie/prix/date des
          passions et powers — ces informations n'ont pas de sens pour un simple souvenir. */}
      {isFeedback ? (
        feedbackMedia.length > 0 && (
          <div style={{ margin: "0 16px 12px", borderRadius: 16, overflow: "hidden" }}>
            <div className="oltodo-no-scrollbar" style={{ display: "flex", overflowX: "auto", scrollSnapType: "x mandatory", gap: 6 }}>
              {feedbackMedia.map((m, i) => (
                <div key={i} style={{ flex: feedbackMedia.length > 1 ? "0 0 88%" : "0 0 100%", scrollSnapAlign: "start", borderRadius: 14, overflow: "hidden", backgroundColor: "#F0F0F0" }}>
                  {m.media_type === "video" ? (
                    <video src={m.url} controls style={{ width: "100%", maxHeight: 420, display: "block" }} />
                  ) : (
                    <img src={m.url} alt="" style={{ width: "100%", maxHeight: 420, objectFit: "cover", display: "block" }} />
                  )}
                </div>
              ))}
            </div>
          </div>
        )
      ) : isGroupCard ? (
        <div style={{ margin: "0 16px 12px", borderRadius: 16, overflow: "hidden", position: "relative" }}>
          <div
            onClick={() => groupInfo && navigate(post.type === "community" ? `/community/${groupInfo.id}` : `/ritual/${groupInfo.id}`)}
            style={{ height: 200, background: groupInfo?.cover_url ? `url(${groupInfo.cover_url}) center/cover` : (COVER_COLORS[post.type] || COVER_COLORS.community), position: "relative", display: "flex", flexDirection: "column", justifyContent: "flex-end", padding: 14, cursor: "pointer" }}
          >
            <div style={{ color: "white", textShadow: "0 2px 8px rgba(0,0,0,0.4)", marginBottom: 12 }}>
              {isPrivateGroup && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, backgroundColor: "rgba(0,0,0,0.45)", borderRadius: 20, padding: "3px 9px", fontSize: 11, fontWeight: 700, marginBottom: 8 }}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                  {t(lang, "pc_private_group")}
                </span>
              )}
              <div style={{ fontSize: 20, fontWeight: 800 }}>{groupInfo?.name || groupInfo?.title || post.title}</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {(groupInfo?.members || []).length > 0 && (
                  <div style={{ display: "flex" }}>
                    {groupInfo.members.slice(0, 5).map((m: any, i: number) => (
                      <div key={m.id} style={{ width: 28, height: 28, borderRadius: "50%", border: "2px solid white", marginLeft: i > 0 ? -10 : 0, backgroundColor: "#333", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 10, fontWeight: 700 }}>
                        {m.avatar_url ? <img src={m.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(m.name)}
                      </div>
                    ))}
                  </div>
                )}
                <span style={{ backgroundColor: "rgba(0,0,0,0.55)", color: "white", fontSize: 12, fontWeight: 700, borderRadius: 20, padding: "4px 10px" }}>
                  {groupInfo?.members_count || 0} {t(lang, "pc_member")}{Number(groupInfo?.members_count) > 1 ? "s" : ""}
                </span>
              </div>
              <button onClick={joinGroup} disabled={groupJoining || groupStatus !== "none"} style={{ padding: "8px 16px", borderRadius: 20, border: "none", backgroundColor: groupStatus !== "none" ? "rgba(255,255,255,0.25)" : "white", color: groupStatus !== "none" ? "white" : "#0a0a0a", fontSize: 12.5, fontWeight: 700, cursor: groupStatus !== "none" ? "default" : "pointer" }}>
                {groupStatus === "joined" ? t(lang, "pc_joined") : groupStatus === "pending" ? t(lang, "invite_contacts_request_sent") : groupJoining ? "..." : isPrivateGroup ? t(lang, "pc_ask_join") : t(lang, "pc_join")}
              </button>
            </div>
          </div>
        </div>
      ) : isWelcomeCard ? (
        <div style={{ margin: "0 16px 16px", padding: "8px 8px 20px", borderRadius: 16, backgroundColor: "#FAFAFA", border: "1px solid #F0F0F0", textAlign: "center" as const }}>
          <div onClick={() => navigate("/profile/" + post.user_id)} style={{ width: 92, height: 92, borderRadius: "50%", backgroundColor: "#0a0a0a", margin: "16px auto 14px", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 30, overflow: "hidden", cursor: "pointer", border: "3px solid white", boxShadow: "0 4px 16px rgba(0,0,0,0.12)" }}>
            {post.author_avatar ? <img src={post.author_avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(post.author_name)}
          </div>
          {post.description ? (
            <div style={{ padding: "0 20px", fontSize: 15, color: "#333", lineHeight: 1.7, marginBottom: (post.author_passions?.length || post.author_powers?.length) ? 16 : 4 }}>{post.description}</div>
          ) : (
            <div style={{ padding: "0 20px", fontSize: 15, color: "#999", marginBottom: (post.author_passions?.length || post.author_powers?.length) ? 16 : 4 }}>{t(lang, "onboarding_welcome_title")}</div>
          )}
          {(post.author_passions?.length > 0 || post.author_powers?.length > 0) && (
            <div style={{ display: "flex", flexWrap: "wrap" as const, justifyContent: "center", gap: 8, padding: "0 20px" }}>
              {(post.author_passions || []).map((label: string, i: number) => (
                <span key={"pa" + i} style={{ backgroundColor: "#0a0a0a", color: "white", borderRadius: 20, padding: "6px 14px", fontSize: 12.5, fontWeight: 600 }}>{label}</span>
              ))}
              {(post.author_powers || []).map((label: string, i: number) => (
                <span key={"po" + i} style={{ backgroundColor: "white", color: "#0a0a0a", border: "1.5px solid #E2E2E2", borderRadius: 20, padding: "6px 14px", fontSize: 12.5, fontWeight: 600 }}>{label}</span>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div style={{ margin: "0 16px 12px", borderRadius: 16, overflow: "hidden", position: "relative" }}>
          <div
            onClick={isBookable(post) ? () => navigate(`/publication/${post.id}`) : undefined}
            style={{ height: 200, background: post.cover_url ? `url(${post.cover_url}) center/cover` : (COVER_COLORS[post.type] || COVER_COLORS.passion), position: "relative", display: "flex", flexDirection: "column", justifyContent: "flex-end", padding: 14, cursor: isBookable(post) ? "pointer" : "default" }}
          >
            <div style={{ position: "absolute", top: 12, left: 12, backgroundColor: TYPE_COLORS[post.type] || "#333333", borderRadius: 20, padding: "5px 12px", color: "white", fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>{post.category}</div>
            <div style={{ position: "absolute", top: 12, right: 12, backgroundColor: (post.is_free || Number(post.price) === 0) ? "#0a0a0a" : "rgba(0,0,0,0.6)", borderRadius: 20, padding: "5px 12px", color: "white", fontSize: 12, fontWeight: 700 }}>
              {(post.is_free || Number(post.price) === 0) ? (t(lang, "free") || "Gratuit") : `${post.price}€`}
            </div>
            {isBookable(post) && post.max_places > 0 && (
              <div style={{ position: "absolute", bottom: 14, right: 14, display: "flex", alignItems: "center", gap: 6 }}>
                {(post.confirmed_participants || []).length > 0 && (
                  <div style={{ display: "flex" }}>
                    {post.confirmed_participants.slice(0, 3).map((pt: any, i: number) => (
                      <div key={pt.id} style={{ width: 24, height: 24, borderRadius: "50%", border: "2px solid white", marginLeft: i > 0 ? -8 : 0, backgroundColor: "#333", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 9, fontWeight: 700 }}>
                        {pt.avatar_url ? <img src={pt.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(pt.name)}
                      </div>
                    ))}
                  </div>
                )}
                <span style={{ backgroundColor: post.current_places >= post.max_places ? "#c0392b" : "rgba(0,0,0,0.6)", color: "white", fontSize: 11, fontWeight: 700, borderRadius: 20, padding: "4px 10px" }}>
                  {post.current_places >= post.max_places ? t(lang, "move_full") : `${post.current_places}/${post.max_places}`}
                </span>
              </div>
            )}
            <div style={{ color: "white", textShadow: "0 2px 8px rgba(0,0,0,0.4)" }}>
              <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 8 }}>{post.title}</div>
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap" as const }}>
                {post.event_date && (
                  <div style={{ fontSize: 13, opacity: 0.9 }}>{new Date(post.event_date).toLocaleDateString("fr-FR")}</div>
                )}
                {post.event_date && (
                  <div style={{ fontSize: 13, opacity: 0.9 }}>{formatEventTime(post.event_date)}</div>
                )}
                {relativeDayLabel(post.event_date, lang) && (
                  <div style={{ fontSize: 13, opacity: 0.9, fontWeight: 700 }}>{relativeDayLabel(post.event_date, lang)}</div>
                )}
                {/* Uniquement quand le badge avatars+places (ci-dessus, isBookable && max_places)
                    ne s'affiche pas déjà : sinon "{max_places} places" en flux normal chevauche
                    ce badge en position absolute sur un écran étroit, cachant les photos des
                    participants derrière le texte (bug remonté par le fondateur). */}
                {post.max_places > 0 && !isBookable(post) && (
                  <div style={{ fontSize: 13, opacity: 0.9 }}>{post.max_places} {t(lang, "places") || "places"}</div>
                )}
                {/* Pas de max_places (capacité illimitée) : rien n'indiquait jusqu'ici qu'il y a
                    déjà du monde inscrit — seul repère social sur ce type de carte. */}
                {!(post.max_places > 0) && (post.confirmed_participants || []).length > 0 && (
                  <div style={{ fontSize: 13, opacity: 0.9 }}>{post.confirmed_participants.length} {t(lang, "msg_participants") || "participants"}</div>
                )}
                {Number(post.min_score) > 0 && (
                  <div style={{ backgroundColor: "#666666", borderRadius: 20, padding: "2px 10px", fontSize: 12, fontWeight: 700, color: "white" }}>★ {t(lang, "pc_min")} {post.min_score}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lieu — mis en avant si l'événement est rattaché à un lieu OLTODO, avec le badge
          "certifié" quand c'est le cas (ex: une soirée organisée au Regina) */}
      {post.venue_name && (
        <div onClick={() => navigate(`/venue/${post.venue_id}`)} style={{ display: "flex", alignItems: "center", gap: 10, margin: "0 16px 12px", padding: "10px 12px", borderRadius: 14, backgroundColor: "#F8F8F8", border: "1px solid #F0F0F0", cursor: "pointer" }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, backgroundColor: "#0a0a0a", backgroundImage: post.venue_avatar ? `url(${post.venue_avatar})` : "none", backgroundSize: "cover", backgroundPosition: "center", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ fontSize: 13, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{post.venue_name}</span>
              {post.venue_verified && <VerifiedBadge size={13} />}
            </div>
            <div style={{ fontSize: 11, color: "#999" }}>{post.venue_verified ? t(lang, "pc_venue_certified") : (post.venue_type || t(lang, "publish_as_type_venue"))}</div>
          </div>
          <span style={{ fontSize: 11, color: "#0a0a0a", fontWeight: 600, flexShrink: 0 }}>{t(lang, "pc_see_arrow")}</span>
        </div>
      )}

      {/* Actions — adaptées à la nature de la publication : un feedback se "top" ou "envieux"
          (comme une activité se rend "intéressé" ou se "rejoint"), et Inviter (passion/power
          uniquement) permet de relayer l'activité à ses propres amis. Partager vit sur la
          rangée suivante, en face de Commentaires. */}
      {isGroupCard || isWelcomeCard ? (
        <div style={{ display: "flex", alignItems: "center", padding: "10px 12px", borderTop: "1px solid #F8F8F8" }}>
          <button onClick={() => toggleReaction(post, "top")} style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 16px", borderRadius: 20, border: post.my_reactions?.includes("top") ? "none" : "1.5px solid #E2E2E2", backgroundColor: post.my_reactions?.includes("top") ? "#0a0a0a" : "white", color: post.my_reactions?.includes("top") ? "white" : "#555", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            <span style={{ fontSize: 13, lineHeight: 1 }}>★</span>
            {t(lang, "top")}
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderTop: "1px solid #F8F8F8" }}>
          {!isFeedback && (
            <ActionButton active={!!post.my_reactions?.includes("interested")} onClick={() => toggleReaction(post, "interested")}>
              {t(lang, "interested") || "Intéressé"}
            </ActionButton>
          )}
          {!isFeedback && post.user_id !== user?.id && (
            <ActionButton
              primary
              disabled={!!post.my_participation_status || isPastEvent(post)}
              onClick={() => !isPastEvent(post) && participate?.(post)}
            >
              {isPastEvent(post) && !post.my_participation_status ? t(lang, "pc_finished") : post.my_participation_status === "confirmed" ? t(lang, "pc_registered") : post.my_participation_status === "pending" ? t(lang, "pc_request_sent_short") : actionLabel(post, lang)}
            </ActionButton>
          )}
          {!isFeedback && (
            <ActionButton icon={<InviteIcon />} onClick={() => setShowInvite(true)}>
              {t(lang, "date_prompt_cta")}
            </ActionButton>
          )}
          {isFeedback && (
            <ActionButton active={!!post.my_reactions?.includes("top")} icon={<span style={{ fontSize: 13, lineHeight: 1 }}>★</span>} onClick={() => toggleReaction(post, "top")}>
              {t(lang, "top")}
            </ActionButton>
          )}
          {isFeedback && (
            <ActionButton active={!!post.my_reactions?.includes("envieux")} icon={<FlameIcon filled={!!post.my_reactions?.includes("envieux")} />} onClick={() => toggleReaction(post, "envieux")}>
              {t(lang, "envieux")}
            </ActionButton>
          )}
        </div>
      )}

      {/* Résumé "qui a réagi" — façon Instagram/Facebook, tappable pour voir la liste */}
      {(post.tops_count > 0 || post.envieux_count > 0 || post.interested_count > 0) && (
        <div style={{ display: "flex", gap: 14, padding: "0 16px 8px", flexWrap: "wrap" as const }}>
          {post.tops_count > 0 && (
            <span onClick={() => setReactorsType("top")} style={{ fontSize: 12, color: "#999", fontWeight: 600, cursor: "pointer" }}>
              {post.tops_count} {t(lang, "pc_count_top")}{post.tops_count > 1 ? "s" : ""}
            </span>
          )}
          {post.envieux_count > 0 && (
            <span onClick={() => setReactorsType("envieux")} style={{ fontSize: 12, color: "#999", fontWeight: 600, cursor: "pointer" }}>
              {post.envieux_count} {t(lang, "pc_count_envieux")}
            </span>
          )}
          {post.interested_count > 0 && (
            <span onClick={() => setReactorsType("interested")} style={{ fontSize: 12, color: "#999", fontWeight: 600, cursor: "pointer" }}>
              {post.interested_count} {t(lang, "pc_count_interested")}{post.interested_count > 1 ? "s" : ""}
            </span>
          )}
        </div>
      )}

      {/* Commentaires (gauche) / Partager (droite) — toujours sous la rangée d'actions */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderTop: "1px solid #F8F8F8", padding: "8px 16px 14px" }}>
        <button onClick={toggleComments} style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: "#888", fontSize: 13, fontWeight: 600, padding: "4px 0" }}>
          <CommentIcon /> {t(lang, "pc_comments")}{commentsCount > 0 ? ` (${commentsCount})` : ""}
        </button>
        <button onClick={share} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 0", border: "none", cursor: "pointer", backgroundColor: "transparent", color: "#888", fontSize: 13, fontWeight: 600 }}>
          <ShareIcon /> {shareConfirm ? t(lang, "pc_link_copied") : (t(lang, "share") || "Partager")}
        </button>
      </div>

      {showInvite && <InvitePostModal postId={post.id} onClose={() => setShowInvite(false)} lang={lang} />}

      {showSponsorModal && (
        <SponsorModal targetType="publication" targetId={post.id} targetLabel={post.title || post.description || t(lang, "pc_publication")} onClose={() => setShowSponsorModal(false)} />
      )}

      {reactorsType && (
        <ReactorsListModal
          title={reactorsType === "top" ? `${t(lang, "top")} · ${post.tops_count}` : reactorsType === "envieux" ? `${t(lang, "envieux")} · ${post.envieux_count}` : `${t(lang, "interested")} · ${post.interested_count}`}
          load={() => api.get(`/publications/${post.id}/reactions?type=${reactorsType}`).then(d => d?.users || [])}
          onClose={() => setReactorsType(null)}
        />
      )}

      {/* Commentaires — texte ou vocal (10s max) */}
      <div style={{ padding: "0 16px 14px" }}>

        {showComments && (
          <div style={{ marginTop: 8 }}>
            {loadingComments && <div style={{ fontSize: 13, color: "#999", padding: "8px 0" }}>{t(lang, "loading_ellipsis")}</div>}
            {!loadingComments && comments.length === 0 && (
              <div style={{ fontSize: 13, color: "#999", padding: "6px 0" }}>{t(lang, "pc_no_comments")}</div>
            )}
            {comments.filter(c => !c.parent_id).map(c => {
              const replies = comments.filter(r => r.parent_id === c.id);
              return (
                <div key={c.id}>
                  {renderComment(c)}
                  {replies.map(r => renderComment(r, true))}
                </div>
              );
            })}

            {replyingTo && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", backgroundColor: "#F5F5F5", borderRadius: 10, padding: "6px 10px", marginTop: 8 }}>
                <span style={{ fontSize: 12, color: "#666" }}>{t(lang, "pc_reply_to")} <strong>{replyingTo.user_name}</strong></span>
                <button onClick={() => setReplyingTo(null)} style={{ border: "none", background: "none", cursor: "pointer", color: "#999", fontSize: 14 }}>✕</button>
              </div>
            )}

            {voicePreview ? (
              // Étape d'écoute avant envoi — jamais d'envoi automatique à l'arrêt de
              // l'enregistrement ; deux boutons libellés sans ambiguïté (pas juste des icônes).
              <div style={{ marginTop: 10, backgroundColor: "#F8F8F8", borderRadius: 16, padding: 10 }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: "#999", marginBottom: 6 }}>{t(lang, "msg_your_voice_message")}</div>
                <CommentAudioPlayer src={voicePreview.url} />
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <button onClick={discardVoicePreview} disabled={sendingVoice} style={{ flex: 1, padding: "9px", borderRadius: 12, border: "1.5px solid #E2E2E2", backgroundColor: "white", color: "#555", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>
                    {t(lang, "msg_restart")}
                  </button>
                  <button onClick={sendVoicePreview} disabled={sendingVoice} style={{ flex: 1, padding: "9px", borderRadius: 12, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 12.5, fontWeight: 700, cursor: "pointer", opacity: sendingVoice ? 0.6 : 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                    {sendingVoice ? t(lang, "msg_sending_short") : <>{t(lang, "send")} <SendIcon /></>}
                  </button>
                </div>
              </div>
            ) : recording ? (
              // Enregistrement en cours — anneau de progression (30s max côté Live, 10s ici),
              // décompte visible et deux actions sans ambiguïté : arrêter, ou annuler d'un coup.
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, backgroundColor: "#FBEAEA", borderRadius: 20, padding: "8px 14px" }}>
                <style>{`@keyframes oltodo-pulse { 0%, 100% { opacity: 1 } 50% { opacity: 0.3 } }`}</style>
                <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#c0392b", flexShrink: 0, animation: "oltodo-pulse 1s ease-in-out infinite" }} />
                <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: "#c0392b", fontVariantNumeric: "tabular-nums" }}>
                  {t(lang, "pc_recording")} {(VOICE_COMMENT_MAX_MS / 1000 - Math.floor(recordMs / 1000))}s
                </span>
                <button onClick={cancelRecording} title={t(lang, "cancel")} style={{ width: 30, height: 30, borderRadius: "50%", border: "1.5px solid #e0b8b8", backgroundColor: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: "#c0392b", fontSize: 13 }}>✕</button>
                <RecordRingButton progress={recordMs / VOICE_COMMENT_MAX_MS} onClick={stopRecording} lang={lang} />
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                <input value={commentText} onChange={e => setCommentText(e.target.value)} onKeyDown={e => e.key === "Enter" && submitTextComment()} placeholder={replyingTo ? t(lang, "pc_reply_to_ph").replace("{name}", replyingTo.user_name) : t(lang, "pc_add_comment_ph")} style={{ flex: 1, padding: "9px 14px", borderRadius: 20, border: "1px solid #E0E0E0", fontSize: 13, outline: "none", backgroundColor: "#F8F8F8" }} />
                {commentText.trim() ? (
                  <button onClick={submitTextComment} disabled={submitting} style={{ width: 34, height: 34, borderRadius: "50%", border: "none", backgroundColor: "#0a0a0a", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><SendIcon /></button>
                ) : (
                  <button onClick={startRecording} disabled={submitting} style={{ width: 34, height: 34, borderRadius: "50%", border: "none", backgroundColor: "#0a0a0a", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }} title={t(lang, "pc_voice_comment_title")}><MicIcon /></button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {extraFooter}
    </div>
  );
}
